import { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Lock, LogIn, LogOut, LayoutDashboard, Loader2 } from "lucide-react";
import { ownerLogin, ownerGoogleLogin, getOwnerToken, setOwnerToken } from "@/lib/api";
import { cn } from "@/lib/utils";
import { useShop } from "@/context/ShopContext";

import OwnerOrders from "@/components/owner/OwnerOrders";
import OwnerReports from "@/components/owner/OwnerReports";
import OwnerInsights from "@/components/owner/OwnerInsights";
import OwnerOperations from "@/components/owner/OwnerOperations";
import OwnerCalendar from "@/components/owner/OwnerCalendar";
import OwnerPromos from "@/components/owner/OwnerPromos";
import OwnerPrices from "@/components/owner/OwnerPrices";
import OwnerCustomers from "@/components/owner/OwnerCustomers";
import OwnerCommunity from "@/components/owner/OwnerCommunity";
import OwnerReferrals from "@/components/owner/OwnerReferrals";
import OwnerBlocked from "@/components/owner/OwnerBlocked";
import OwnerSettings from "@/components/owner/OwnerSettings";
import OwnerAppearance from "@/components/owner/OwnerAppearance";
import OwnerBanners from "@/components/owner/OwnerBanners";
import OwnerTabs from "@/components/owner/OwnerTabs";
import OwnerContent from "@/components/owner/OwnerContent";

const AUTH_HOST = "https://auth.emergentagent.com/";
const extractSessionId = (url) => url?.match(/[?#&]session_id=([^&#]+)/)?.[1] ?? null;

const TABS = [
  { key: "orders", label: "Orders", C: OwnerOrders },
  { key: "operations", label: "Operations", C: OwnerOperations },
  { key: "calendar", label: "Calendar", C: OwnerCalendar },
  { key: "insights", label: "Insights", C: OwnerInsights },
  { key: "reports", label: "Reports", C: OwnerReports },
  { key: "promos", label: "Promos", C: OwnerPromos },
  { key: "prices", label: "Prices", C: OwnerPrices },
  { key: "customers", label: "Customers", C: OwnerCustomers },
  { key: "community", label: "Community", C: OwnerCommunity },
  { key: "referrals", label: "Referrals", C: OwnerReferrals },
  { key: "blocked", label: "Blocked", C: OwnerBlocked },
  { key: "settings", label: "Settings", C: OwnerSettings },
  { key: "appearance", label: "Appearance", C: OwnerAppearance },
  { key: "banners", label: "Banners", C: OwnerBanners },
  { key: "content", label: "Content", C: OwnerContent },
  { key: "tabs", label: "Tab Manager", C: OwnerTabs },
];

export default function Owner() {
  const queryClient = useQueryClient();
  const { shop } = useShop();
  const [authed, setAuthed] = useState(!!getOwnerToken());
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [tab, setTab] = useState("orders");

  // Owner Google callback on /owner
  useEffect(() => {
    const sid = extractSessionId(window.location.href);
    if (sid && !getOwnerToken()) {
      setBusy(true);
      ownerGoogleLogin(sid)
        .then(() => {
          setAuthed(true);
          const url = new URL(window.location.href);
          url.searchParams.delete("session_id");
          url.hash = url.hash.replace(/([?#&])session_id=[^&#]+&?/, "$1").replace(/[?#&]$/, "");
          window.history.replaceState(window.history.state, "", url.toString());
        })
        .catch((e) => toast.error(e.message || "Google sign-in failed"))
        .finally(() => setBusy(false));
    }
  }, []);

  const login = async () => {
    setBusy(true);
    try {
      await ownerLogin(username.trim(), password);
      setAuthed(true);
      toast.success("Welcome back");
    } catch (e) {
      toast.error(e.message || "Incorrect username or passcode");
    } finally {
      setBusy(false);
    }
  };

  const googleLogin = () => {
    window.location.href = `${AUTH_HOST}?redirect=${encodeURIComponent(`${window.location.origin}/owner`)}`;
  };

  const logout = () => {
    setOwnerToken(null);
    setAuthed(false);
    queryClient.clear();
  };

  if (!authed) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background px-4">
        <div className="w-full max-w-sm rounded-3xl border border-border bg-card p-8">
          <div className="mb-6 flex flex-col items-center gap-2 text-center">
            <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-primary text-primary-foreground"><Lock className="h-6 w-6" /></div>
            <h1 className="font-display text-xl font-bold">Owner Portal</h1>
            <p className="text-sm text-muted-foreground">{shop?.name || "Dry Cleaners Goldys"}</p>
          </div>
          <div className="space-y-3">
            <input value={username} onChange={(e) => setUsername(e.target.value)} placeholder="Username" data-testid="owner-username" className="w-full rounded-xl border border-border bg-background p-3 text-sm outline-none focus:border-primary" />
            <input value={password} onChange={(e) => setPassword(e.target.value)} onKeyDown={(e) => e.key === "Enter" && login()} type="password" placeholder="Passcode" data-testid="owner-password" className="w-full rounded-xl border border-border bg-background p-3 text-sm outline-none focus:border-primary" />
            <button onClick={login} disabled={busy || !username || !password} data-testid="owner-login-button" className="flex w-full items-center justify-center gap-2 rounded-full bg-primary py-3 text-sm font-semibold text-primary-foreground disabled:opacity-50">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><LogIn className="h-4 w-4" /> Sign in</>}
            </button>
            <div className="flex items-center gap-3 py-1"><div className="h-px flex-1 bg-border" /><span className="text-xs text-muted-foreground">or</span><div className="h-px flex-1 bg-border" /></div>
            <button onClick={googleLogin} data-testid="owner-google-login" className="w-full rounded-full border border-border py-3 text-sm font-semibold">Sign in with Google</button>
          </div>
        </div>
      </div>
    );
  }

  const ActiveTab = TABS.find((t) => t.key === tab)?.C || OwnerOrders;

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-40 glass border-b border-border">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-3">
          <div className="flex items-center gap-2">
            <LayoutDashboard className="h-5 w-5 text-primary" />
            <h1 className="font-display text-base font-bold">Owner Dashboard</h1>
          </div>
          <button onClick={logout} data-testid="owner-logout" className="inline-flex items-center gap-1 rounded-full border border-border px-3 py-1.5 text-xs font-semibold"><LogOut className="h-3.5 w-3.5" /> Sign out</button>
        </div>
        <div className="mx-auto max-w-7xl overflow-x-auto no-scrollbar px-4 pb-2">
          <div className="flex gap-1">
            {TABS.map((t) => (
              <button key={t.key} onClick={() => setTab(t.key)} data-testid={`owner-tab-${t.key}`} className={cn("whitespace-nowrap rounded-full px-4 py-1.5 text-sm font-medium transition", tab === t.key ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-secondary")}>
                {t.label}
              </button>
            ))}
          </div>
        </div>
      </header>
      <main className="mx-auto max-w-7xl px-4 py-6">
        <ActiveTab />
      </main>
    </div>
  );
}
